// Judgment-lens fill of the shared kanta/salkku overview vocabulary (`lib/overview-lens.ts`) —
// turns an already-loaded, already-scoped `Finding[]` (the single URL-scoped `GET /findings`,
// narrowed by that lens's `category`/`source_type` perspective) into the same
// `OverviewBucket[]` / `PerBuildingOverview` shape Konfiguraatio's setup lens produces, so Datan
// laatu, Talotekniikan toimivuus and Sisäilmaolosuhteet all render through the one shared
// `PerspectiveOverview` (docs/spec/concepts.md §2.4 → "Portfolio-scope overview grammar" →
// judgment-lens fill). No React, no fetch — fetching lives in the paired hook,
// `useLensFindingsOverview.ts`.
import { AlertTriangle, AlertCircle, Info, CheckCheck } from "lucide-react";

import type { Building, Finding } from "@/lib/api/building";
import type { CardTone } from "@/lib/card-styles";
import {
  deriveOverviewBuckets,
  type OverviewLensState,
} from "@/lib/overview-lens";

export type FindingBucketKey =
  "kriittiset" | "varoitukset" | "huomiot" | "kuitatut";

/** Exhaustive over every non-resolved finding: `open` + severity for the first three, then
 *  `acknowledged` regardless of severity — no thresholds, no magic numbers, every input a field
 *  every `Finding` already carries. */
export const FINDING_BUCKET_ORDER: FindingBucketKey[] = [
  "kriittiset",
  "varoitukset",
  "huomiot",
  "kuitatut",
];

const FINDING_BUCKET_META: Record<
  FindingBucketKey,
  { label: string; icon: typeof AlertTriangle; tone: CardTone }
> = {
  kriittiset: { label: "Kriittisiä", icon: AlertTriangle, tone: "warn" },
  varoitukset: { label: "Varoituksia", icon: AlertCircle, tone: "warn" },
  huomiot: { label: "Huomioita", icon: Info, tone: "warn" },
  kuitatut: { label: "Kuitattu, ei ratkaistu", icon: CheckCheck, tone: "warn" },
};

/** Which bucket (if any) this finding belongs to. `resolved` findings belong to none — they are
 *  no longer part of "what still needs work in this scope". */
function bucketKeyForFinding(finding: Finding): FindingBucketKey | null {
  if (finding.status === "acknowledged") return "kuitatut";
  if (finding.status !== "open") return null;
  if (finding.severity === "critical") return "kriittiset";
  if (finding.severity === "warning") return "varoitukset";
  if (finding.severity === "info") return "huomiot";
  return null;
}

/**
 * `findings === null` means the scoped `GET /findings` request hasn't settled yet — every
 * building is reported `dataAvailable: false` for that render, never fabricated as clean. Once
 * findings load (even to an empty array — no findings in scope is a real, complete state), every
 * scoped building gets a real per-building bucket membership from the same flat list.
 */
export function deriveLensFindingsOverview(
  scopedBuildings: Building[],
  findings: Finding[] | null,
): OverviewLensState {
  const dataAvailable = findings !== null;

  const findingsByBuilding = new Map<string, Finding[]>();
  if (dataAvailable) {
    for (const f of findings) {
      const rows = findingsByBuilding.get(f.building_id) ?? [];
      rows.push(f);
      findingsByBuilding.set(f.building_id, rows);
    }
  }

  const perBuilding = new Map(
    scopedBuildings.map((b) => {
      const rows = findingsByBuilding.get(b.id) ?? [];
      const openQuestions = FINDING_BUCKET_ORDER.filter((key) =>
        rows.some((f) => bucketKeyForFinding(f) === key),
      );
      return [
        b.id,
        {
          buildingId: b.id,
          openQuestions,
          complete: dataAvailable && openQuestions.length === 0,
          dataAvailable,
        },
      ] as const;
    }),
  );

  const buckets = deriveOverviewBuckets(
    scopedBuildings,
    perBuilding,
    FINDING_BUCKET_ORDER,
    (key) => FINDING_BUCKET_META[key],
  );

  return {
    loading: !dataAvailable,
    settledCount: dataAvailable ? scopedBuildings.length : 0,
    totalCount: scopedBuildings.length,
    scopedBuildings,
    buckets,
    perBuilding,
  };
}
